import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Button from './Button';

const Hero = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1,
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 80,
        damping: 12
      }
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center bg-gradient-to-b from-gray-50 via-white to-gray-50">
      {/* Subtle Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:24px_24px]" />
      
      <div className="relative max-w-7xl mx-auto px-6 py-24 w-full">
        <motion.div 
          className="grid lg:grid-cols-2 gap-12 items-center"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Left Content */}
          <motion.div className="space-y-8" variants={itemVariants}>
            {/* Badge */}
            <motion.div
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/5 rounded-full border border-primary/10"
              whileHover={{ scale: 1.02 }}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              <span className="text-xs font-medium text-primary uppercase tracking-wider">Alpha Laser Technologies | ISO 9001:2015 Certified | Coimbatore</span>
            </motion.div>

            {/* Heading */}
            <motion.div className="space-y-4" variants={itemVariants}>
              <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold text-steel leading-[1.1] tracking-tight">
                Precision{" "}
                <span className="text-primary">Laser Cutting</span>{" "}
                <span className="text-secondary">& Fabrication</span>
                <br />
                <span className="text-3xl lg:text-4xl xl:text-5xl">Services in Coimbatore</span>
              </h1>
              
              <p className="text-lg lg:text-xl text-steel-lighter leading-relaxed max-w-xl">
                <strong>Alpha Laser Technologies</strong> - South India's trusted partner for <strong>industrial laser cutting</strong>, <strong>CNC laser services</strong>, and <strong>custom metal fabrication</strong>. Based in Coimbatore, serving all over TamilNadu with ±0.1mm precision accuracy.
              </p>
            </motion.div>
            
            {/* CTA Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-4"
              variants={itemVariants}
            >
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Link to="/contact">
                  <Button variant="secondary" size="lg" className="px-8 shadow-lg shadow-secondary/20">
                    Get Free Quote in 24 Hours
                  </Button>
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Link to="/services">
                  <Button variant="outline" size="lg" className="px-8">
                    View Our Services
                  </Button>
                </Link>
              </motion.div>
            </motion.div>
            
            {/* Stats Grid */}
            <motion.div 
              className="grid grid-cols-2 lg:grid-cols-4 gap-6 pt-8 border-t border-gray-200"
              variants={containerVariants}
            >
              {[
                { value: "±0.1mm", label: "Precision Accuracy" },
                { value: "24-48h", label: "Fast Turnaround" },
                { value: "100+", label: "Industrial Clients" },
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="text-center lg:text-left"
                >
                  <div className="text-2xl lg:text-3xl font-bold text-steel">{stat.value}</div>
                  <div className="text-sm text-steel-lighter mt-1">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
          
          {/* Right Content - Image */}
          <motion.div 
            className="relative"
            variants={itemVariants}
          >
            {/* Main Image */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <div className="aspect-[4/3] bg-gray-100">
                <img 
                  src="https://tqxjvkixxssyxenzmjtq.supabase.co/storage/v1/object/public/Alpha/Assets/Flatlasersheet/flatsheet1.png" 
                  alt="Industrial fiber laser cutting machine for precision metal cutting services in Coimbatore - Alpha Laser Technologies"
                  title="BODOR Fiber Laser Cutting Machine - Precision Laser Cutting Services"
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 via-transparent to-secondary/10 pointer-events-none" />
            </div>
            
            {/* ISO Badge */}
            <motion.div
              className="absolute -bottom-4 -left-4 z-10"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              whileHover={{ scale: 1.05 }}
            >
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-3 flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <div>
                  <div className="text-sm font-semibold text-steel">ISO Certified</div>
                  <div className="text-xs text-steel-lighter">Quality Assured</div>
                </div>
              </div>
            </motion.div>

            {/* Accuracy Badge */}
            <motion.div
              className="absolute -top-4 -right-4 z-10"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.05 }}
            >
              <div className="bg-gradient-to-br from-secondary to-red-600 text-white rounded-xl shadow-lg p-4 text-center min-w-[100px]">
                <div className="text-3xl font-bold">99.9%</div>
                <div className="text-xs font-medium opacity-90 mt-1">Accuracy</div>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;